import { useState } from 'react'
import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  StatBand,
  Card,
  Icon,
  Badge,
  Reveal,
} from './kit.jsx'

const REASONS = [
  'See the 10,942-company database filtered to your industry and region live.',
  'Get pricing and rollout for a team of reps across multiple territories.',
  'Walk through connecting your own Gmail for outreach and reply tracking.',
  'Talk through migrating off scraped lists, spreadsheets, or a blast tool.',
]

const TRUST = [
  { value: '10,942', label: 'Verified B2B leads' },
  { value: '< 1 day', label: 'Typical reply from sales' },
  { value: '50', label: 'U.S. regions covered' },
]

const FIELD = {
  border: '1px solid var(--mk-line)',
  borderRadius: 10,
  padding: '11px 13px',
  fontSize: 14,
  width: '100%',
  background: 'var(--mk-surface-soft)',
  color: 'var(--mk-ink)',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}

const LABEL = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--mk-ink)',
  marginBottom: 7,
}

export default function ContactSalesPage() {
  const [sent, setSent] = useState(false)

  function handleSubmit(e) {
    e.preventDefault()
    setSent(true)
  }

  return (
    <MarketingShell active="">
      <Seo
        title="Contact sales & book a demo"
        description="Talk to the Revailo Sales team — book a walkthrough of 10,942 verified B2B leads, outreach sequences, and pipeline, tailored to your industry and region."
        path="/contact"
        keywords="contact Revailo Sales, book a demo, b2b leads demo, sales software, verified b2b leads"
      />

      <PageHero
        eyebrow="Contact"
        title={
          <>
            Talk to the team behind <br className="hidden sm:block" />
            the B2B lead engine.
          </>
        }
        lead="Tell us your industry, your territory, and how your team sells today. We will show you exactly what Revailo Sales looks like loaded with your market — no slideshow, no pressure."
      />

      <Section sm>
        <Container>
          <Reveal>
            <div className="grid grid-cols-1 items-center gap-10 min-[960px]:grid-cols-2 min-[960px]:gap-16" style={{ alignItems: 'start' }}>
              {/* Left — intro, reasons, contact methods */}
              <div>
                <div className="inline-flex items-center gap-x-2 text-[12px] font-bold uppercase tracking-[0.14em] text-[var(--mk-accent-dark)]">
                  <span className="h-[6px] w-[6px] rounded-full bg-[var(--mk-accent)]" />
                  Why talk to us
                </div>
                <h2 className="m-0 mt-4 text-[42px] font-semibold leading-[1.07] tracking-[-0.022em] text-[var(--mk-ink)] max-[720px]:text-[30px]">
                  A demo built around your market
                </h2>
                <p className="m-0 mt-4 text-[18px] leading-[1.6] text-[var(--mk-muted)] max-[720px]:text-[16px]">
                  Every walkthrough is run against the real database — 10,942 verified companies,
                  organized by industry and region. You will see
                  your accounts, not a sandbox.
                </p>

                <ul className="m-0 mt-6 flex list-none flex-col gap-y-3 p-0">
                  {REASONS.map((r) => (
                    <li
                      key={r}
                      className="flex items-start gap-x-3 text-[15px] leading-[1.5] text-[var(--mk-ink-soft)]"
                    >
                      <span className="mt-px inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[var(--mk-accent-tint)] text-[var(--mk-accent-dark)]">
                        <Icon name="check" size={13} />
                      </span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>

                <div style={{ display: 'grid', gap: 14, marginTop: 32 }}>
                  <Card soft>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-[14px] bg-[var(--mk-accent-tint)] text-[var(--mk-accent-dark)]">
                        <Icon name="mail" size={22} />
                      </span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--mk-ink)' }}>
                          Email sales
                        </div>
                        <a
                          href="mailto:sales@revailo-sales.com"
                          style={{ fontSize: 14, color: 'var(--mk-accent-dark)', fontWeight: 600 }}
                        >
                          sales@revailo-sales.com
                        </a>
                      </div>
                    </div>
                  </Card>
                  <Card soft>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-[14px] bg-[var(--mk-accent-tint)] text-[var(--mk-accent-dark)]">
                        <Icon name="clock" size={22} />
                      </span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--mk-ink)' }}>
                          Book a demo
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--mk-muted)' }}>
                          Fill out the form and we will send a 30-minute slot — usually the same day.
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Right — contact form */}
              <div>
                <Card style={{ padding: 28 }}>
                  {sent ? (
                    <div style={{ textAlign: 'center', padding: '36px 8px' }}>
                      <span
                        className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-[14px] bg-[var(--mk-accent-tint)] text-[var(--mk-accent-dark)]"
                        style={{ margin: '0 auto 18px', width: 52, height: 52 }}
                      >
                        <Icon name="check" size={26} />
                      </span>
                      <h3 className="m-0 text-[26px] font-semibold leading-[1.15] tracking-[-0.022em] text-[var(--mk-ink)] max-[720px]:text-[22px]">
                        Thanks — we will be in touch.
                      </h3>
                      <p className="m-0 mt-4 text-[18px] leading-[1.6] text-[var(--mk-muted)] max-[720px]:text-[16px]">
                        Your message is with our sales team. Expect a reply at your work email within
                        one business day, usually much sooner.
                      </p>
                      <div style={{ marginTop: 24 }}>
                        <button
                          type="button"
                          className="inline-flex cursor-pointer items-center justify-center gap-x-2 whitespace-nowrap rounded-full border border-[var(--mk-line)] bg-white px-5 py-3 text-[14px] font-semibold text-[var(--mk-ink)] transition-[background-color,color,border-color,transform] duration-150 hover:border-[var(--mk-ghost)] hover:bg-[var(--mk-surface-soft)] active:translate-y-px"
                          onClick={() => setSent(false)}
                        >
                          Send another message
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div style={{ marginBottom: 8 }}>
                        <Badge tone="accent">Contact sales</Badge>
                      </div>
                      <h3
                        className="m-0 mt-2 text-[26px] font-semibold leading-[1.15] tracking-[-0.022em] text-[var(--mk-ink)] max-[720px]:text-[22px]"
                        style={{ marginBottom: 20 }}
                      >
                        Tell us about your business
                      </h3>

                      <div style={{ display: 'grid', gap: 16 }}>
                        <div>
                          <label style={LABEL} htmlFor="cf-name">
                            Name
                          </label>
                          <input
                            id="cf-name"
                            name="name"
                            type="text"
                            required
                            placeholder="Marcus Bell"
                            style={FIELD}
                          />
                        </div>

                        <div>
                          <label style={LABEL} htmlFor="cf-email">
                            Work email
                          </label>
                          <input
                            id="cf-email"
                            name="email"
                            type="email"
                            required
                            placeholder="you@company.com"
                            style={FIELD}
                          />
                        </div>

                        <div>
                          <label style={LABEL} htmlFor="cf-company">
                            Company
                          </label>
                          <input
                            id="cf-company"
                            name="company"
                            type="text"
                            required
                            placeholder="Bell Analytics Inc."
                            style={FIELD}
                          />
                        </div>

                        <div>
                          <label style={LABEL} htmlFor="cf-team">
                            Team size
                          </label>
                          <select id="cf-team" name="teamSize" defaultValue="" style={FIELD}>
                            <option value="" disabled>
                              Select team size
                            </option>
                            <option value="1">Just me</option>
                            <option value="2-5">2–5 reps</option>
                            <option value="6-20">6–20 reps</option>
                            <option value="21-50">21–50 reps</option>
                            <option value="50+">50+ reps</option>
                          </select>
                        </div>

                        <div>
                          <label style={LABEL} htmlFor="cf-message">
                            What are you trying to do?
                          </label>
                          <textarea
                            id="cf-message"
                            name="message"
                            rows={4}
                            placeholder="We sell into three regions and want to move off scraped lists."
                            style={{ ...FIELD, resize: 'vertical', minHeight: 96 }}
                          />
                        </div>

                        <button
                          type="submit"
                          className="inline-flex cursor-pointer items-center justify-center gap-x-2 whitespace-nowrap rounded-full border border-transparent bg-[var(--mk-accent)] px-[26px] py-[14px] text-[15px] font-semibold text-white transition-[background-color,color,border-color,transform] duration-150 hover:bg-[var(--mk-accent-dark)] active:translate-y-px"
                          style={{ width: '100%' }}
                        >
                          Request a demo
                        </button>

                        <p
                          style={{
                            fontSize: 12.5,
                            color: 'var(--mk-muted)',
                            textAlign: 'center',
                            margin: 0,
                          }}
                        >
                          No credit card required. We reply from a real person, not a bot.
                        </p>
                      </div>
                    </form>
                  )}
                </Card>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <StatBand stats={TRUST} />
          </Reveal>
        </Container>
      </Section>
    </MarketingShell>
  )
}
